import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const LangContext = createContext(null);

export const CITY_LABELS = {
  madrid:   { en: "Madrid",   es: "Madrid",   country: "spain" },
  sevilla:  { en: "Seville",  es: "Sevilla",  country: "spain" },
  campania: { en: "Campania", es: "Campania", country: "italy" },
  roma:     { en: "Rome",     es: "Roma",     country: "italy" },
  lisboa:   { en: "Lisbon",   es: "Lisboa",   country: "portugal" },
};

export const COUNTRY_LABELS = {
  spain:    { en: "Spain",    es: "España" },
  italy:    { en: "Italy",    es: "Italia" },
  portugal: { en: "Portugal", es: "Portugal" },
};

export const CATEGORY_LABELS = {
  adventure:  { en: "Adventure",  es: "Aventura" },
  wine:       { en: "Wine",       es: "Vino" },
  gastronomy: { en: "Gastronomy", es: "Gastronomía" },
  workshop:   { en: "Workshop",   es: "Taller" },
  cultural:   { en: "Cultural",   es: "Cultural" },
  romantic:   { en: "Romantic",   es: "Romántico" },
  family:     { en: "Family",     es: "Familia" },
};

export function cityLabel(slug, lang = "en") {
  return (CITY_LABELS[slug] && CITY_LABELS[slug][lang]) || slug;
}
export function countryLabel(slug, lang = "en") {
  return (COUNTRY_LABELS[slug] && COUNTRY_LABELS[slug][lang]) || slug;
}
export function categoryLabel(slug, lang = "en") {
  return (CATEGORY_LABELS[slug] && CATEGORY_LABELS[slug][lang]) || slug;
}

const STRINGS = {
  en: {
    nav: { spain: "Spain", italy: "Italy", portugal: "Portugal", experiences: "Experiences", collection: "The Collection", contact: "Contact" },
    cta: { explore: "Explore the collection", contact: "Get in touch", back_to: "Back to", discover: "Discover", see_all: "See all experiences", clear: "Clear filters" },
    filters: {
      title: "Filters",
      country: "Country",
      city: "City",
      category: "Category",
      all: "All",
      showing: "Showing",
      of: "of",
      empty_title: "No experiences match this filter.",
      empty_sub: "Try removing a filter or starting over.",
    },
    home: {
      eyebrow: "Spain · Italy · Portugal",
      headline_main: "Singular experiences,",
      headline_italic: "truly lived.",
      subtitle: "Twenty stories curated by Viaja de Verdad — across Iberia and Italy.",
      scroll: "Scroll",
      manifesto_eyebrow: "— A travel atelier since 2016 —",
      manifesto_headline_main: "A dossier of twenty,",
      manifesto_headline_italic: "hand-curated.",
      manifesto_p1: "Viaja de Verdad is a small studio of travel designers who refuse to publish what we have not seen, slept in, eaten in, or walked away from with friends on first-name terms.",
      manifesto_p2: "Five cities. Twenty experiences. Each one tested in person before it earns a place in the dossier — a wine pour, a horseback dawn, a candlelit Roman bath, a glove cut to the shape of your hand.",
      manifesto_p3: "This is the public face of work normally kept off the open market. A showcase, not a catalogue. The rest, by introduction.",
      destinations_eyebrow: "Three countries",
      destinations_headline_main: "Iberia,",
      destinations_headline_italic: "and Italy.",
      featured_eyebrow: "From the dossier",
      featured_headline_main: "Six,",
      featured_headline_italic: "of twenty.",
      quote_text: "Travel as a craft. Not a checklist.",
      quote_attr: "The Viaja de Verdad Studio",
      final_cta_eyebrow: "A private dossier",
      final_cta_main: "Twenty stories,",
      final_cta_italic: "to be lived.",
      final_cta_sub: "The full collection awaits inside.",
    },
    collection: {
      eyebrow: "What we curate",
      title: "The Collection",
      subtitle: "Twenty singular experiences, composed by hand.",
      intro_eyebrow: "— A small dossier —",
      intro_title: "Twenty, never two hundred.",
      intro_p1: "We could publish a thousand experiences and we choose to publish twenty. Each one has been visited and verified by the studio, on weather windows that suit the season.",
      intro_p2: "Filter by country, by city, by the way you like to spend an afternoon. The order is not arbitrary: it reflects the rhythm in which we send our travelers through Iberia and Italy.",
      count_label: "experiences",
    },
    about: {
      hero_eyebrow: "About this collection",
      hero_title: "Twenty stories. Three countries.",
      hero_italic: "One craft.",
      hero_sub: "Viaja de Verdad has been designing private journeys across Iberia and Italy since 2016. This is the public side of work that lives, for the most part, off the open market.",
      premise_eyebrow: "— The premise —",
      premise_title: "We publish what we have lived.",
      premise_p1: "Nothing enters the dossier from a brochure. The studio visits, sleeps, eats, drinks and walks every experience we sell. If a place loses its hand — a new chef, a new owner, a new pace — it leaves the dossier the same week. Quietly.",
      premise_p2: "We are not a marketplace. We are a small atelier of travel designers — five of us — who would rather offer twenty good stories than two hundred mediocre ones.",
      criteria_eyebrow: "— The criteria —",
      criteria_title: "What earns a place.",
      criteria_items: [
        { k: "Hands, not handlers", v: "Every experience is led by the person who knows the craft: the vintner, the rider, the rope-maker, the chef. Not a guide reading a script." },
        { k: "Verified by the studio", v: "Each entry has been visited in person by a Viaja de Verdad designer within the last twelve months — at the season we recommend it." },
        { k: "Private by default", v: "We work with partners who agree to keep your day yours. No coach parties, no crossed itineraries, no shared tables unless you ask for one." },
        { k: "Sense of place", v: "A castle dinner that could be served anywhere is not a castle dinner. We look for the version of a thing that could only happen in that village, that bay, that vineyard." },
      ],
      geography_eyebrow: "— The geography —",
      geography_title: "Five cities. Three countries.",
      geography_p: "Madrid and Seville for Spain. Rome and Campania for Italy. Lisbon for Portugal. The dossier orbits around hands we trust, in places we can reach in a morning to fix a detail if the weather, or the world, asks us to.",
      studio_eyebrow: "— The studio —",
      studio_title: "Madrid · Lisbon · Rome — and on the road.",
      studio_p1: "We are five travel designers, founded in 2016. We split the year between desk weeks in Madrid, Lisbon and Rome, and field weeks in the partners' homes, kitchens, cellars and stables.",
      studio_p2: "If you are reading this, you have likely already met us — or someone you trust has. The full dossier is by introduction; this site is its public face.",
      cta_eyebrow: "Read what we curate",
      cta_main: "The twenty,",
      cta_italic: "in detail.",
      cta_sub: "Browse the collection by country, by city, or by the way you like to spend a day.",
      cta_button: "See all experiences",
    },
    contact_page: {
      eyebrow: "Contact",
      title: "Write to the studio,",
      italic: "we'll write back.",
      sub: "No forms, no booking widgets. Just a line from you to the desk where the dossier is kept.",
      reach_us: "Reach the studio at",
      bases_label: "Studio bases",
      bases: "Madrid · Lisbon · Rome",
      hours_label: "Studio hours",
      hours: "Monday to Friday, 10:00 – 19:00 CET",
      note: "We reply to every email — usually within a working day, often sooner.",
    },
    country: {
      eyebrow: "A destination",
      by_city_eyebrow: "— By city —",
      cta_eyebrow: "From this country",
      cta_main_pre: "All of",
      cta_main_post: ", ",
      cta_main_italic: "composed.",
      cta_sub: "The complete dossier for this country, in the order we travel it.",
      cta_button: "See all experiences",
    },
    detail: {
      back: "Back to the collection",
      back_to_city: "Back to",
      back_to_country: "Back to",
      curated_by: "Curated with",
      duration: "Duration",
      perfect_for: "Perfect for",
      best_season: "Best season",
      price: "Price",
      highlights: "What you'll do",
      experience_eyebrow_pre: "01 / Experience",
      intro_eyebrow: "— The experience —",
      highlights_eyebrow: "— Highlights —",
      highlights_title_pre: "What makes it",
      highlights_title_italic: "singular.",
      gallery_eyebrow: "— In images —",
      gallery_title_pre: "In",
      gallery_title_italic: "images.",
      curated_eyebrow: "— Curated with —",
      cta_eyebrow: "The full collection",
      cta_main: "Explore the full",
      cta_italic: "collection.",
      cta_sub: "Twenty singular experiences. Three countries. One story at a time.",
      cta_button: "See all experiences",
      related_eyebrow: "— You may also like —",
      related_title_pre: "More from",
    },
    footer: {
      tagline: "Singular experiences across Iberia and Italy.",
      studio_eyebrow: "Studio",
      navigate_eyebrow: "Navigate",
      contact_eyebrow: "Contact",
      founded: "Founded in 2016. Planning from Madrid, Lisbon, Rome and on the road.",
      reach_us: "Reach the studio at",
      legal_privacy: "Privacy",
      legal_cookies: "Cookies",
      legal_legal: "Legal",
    },
    notfound: {
      eyebrow: "Off the map",
      title: "This page does not exist,",
      italic: "yet.",
      sub: "The collection is here.",
      cta: "Back to the collection",
    },
  },
  es: {
    nav: { spain: "España", italy: "Italia", portugal: "Portugal", experiences: "Experiencias", collection: "La Colección", contact: "Contacto" },
    cta: { explore: "Explora la colección", contact: "Contacto", back_to: "Volver a", discover: "Descubrir", see_all: "Ver todas las experiencias", clear: "Limpiar filtros" },
    filters: {
      title: "Filtros",
      country: "País",
      city: "Ciudad",
      category: "Categoría",
      all: "Todas",
      showing: "Mostrando",
      of: "de",
      empty_title: "Ninguna experiencia coincide con este filtro.",
      empty_sub: "Quita un filtro o empieza de nuevo.",
    },
    home: {
      eyebrow: "España · Italia · Portugal",
      headline_main: "Experiencias singulares,",
      headline_italic: "vividas de verdad.",
      subtitle: "Veinte historias curadas por Viaja de Verdad — en Iberia e Italia.",
      scroll: "Bajar",
      manifesto_eyebrow: "— Un taller de viaje desde 2016 —",
      manifesto_headline_main: "Un dossier de veinte,",
      manifesto_headline_italic: "a mano.",
      manifesto_p1: "Viaja de Verdad es un pequeño estudio de diseñadores de viaje que no publica nada que no hayamos visto, dormido, comido o despedido con amigos a los que llamamos por su nombre.",
      manifesto_p2: "Cinco ciudades. Veinte experiencias. Cada una probada en persona antes de entrar en el dossier — una copa servida en bodega, un amanecer a caballo, una cena romana a la luz de las velas, un guante hecho a la medida de tu mano.",
      manifesto_p3: "Esta es la cara pública de un trabajo que normalmente queda fuera del mercado abierto. Un escaparate, no un catálogo. El resto, por presentación.",
      destinations_eyebrow: "Tres países",
      destinations_headline_main: "Iberia,",
      destinations_headline_italic: "e Italia.",
      featured_eyebrow: "Del dossier",
      featured_headline_main: "Seis,",
      featured_headline_italic: "de veinte.",
      quote_text: "Viajar como oficio. No como lista.",
      quote_attr: "El Estudio Viaja de Verdad",
      final_cta_eyebrow: "Un dossier privado",
      final_cta_main: "Veinte historias,",
      final_cta_italic: "para ser vividas.",
      final_cta_sub: "La colección completa te espera dentro.",
    },
    collection: {
      eyebrow: "Lo que curamos",
      title: "La Colección",
      subtitle: "Veinte experiencias singulares, compuestas a mano.",
      intro_eyebrow: "— Un dossier breve —",
      intro_title: "Veinte, nunca doscientas.",
      intro_p1: "Podríamos publicar mil experiencias y elegimos publicar veinte. Cada una ha sido visitada y verificada por el estudio, en la ventana que mejor le sienta.",
      intro_p2: "Filtra por país, por ciudad o por el modo en que te gusta pasar una tarde. El orden no es arbitrario: refleja el ritmo en que enviamos a nuestros viajeros por Iberia e Italia.",
      count_label: "experiencias",
    },
    about: {
      hero_eyebrow: "Sobre la colección",
      hero_title: "Veinte relatos. Tres países.",
      hero_italic: "Un oficio.",
      hero_sub: "Viaja de Verdad diseña viajes privados por Iberia e Italia desde 2016. Esta es la cara pública de un oficio que, en su mayor parte, vive fuera del mercado abierto.",
      premise_eyebrow: "— La premisa —",
      premise_title: "Publicamos lo que hemos vivido.",
      premise_p1: "Nada entra al dossier desde un folleto. El estudio visita, duerme, come, bebe y camina cada experiencia que vende. Si un lugar pierde el pulso — un nuevo chef, un nuevo dueño, un ritmo distinto — sale del dossier la misma semana. En silencio.",
      premise_p2: "No somos un marketplace. Somos un pequeño taller de diseñadores de viaje — cinco — que prefiere ofrecer veinte buenas historias antes que doscientas mediocres.",
      criteria_eyebrow: "— Los criterios —",
      criteria_title: "Qué se gana un sitio en el dossier.",
      criteria_items: [
        { k: "Manos, no intermediarios", v: "Cada experiencia la conduce quien conoce el oficio: el viticultor, el jinete, el guantero, el chef. No un guía leyendo un guion." },
        { k: "Verificada por el estudio", v: "Cada entrada ha sido visitada en persona por un diseñador de Viaja de Verdad en los últimos doce meses — en la temporada en que la recomendamos." },
        { k: "Privada por defecto", v: "Trabajamos con socios que se comprometen a guardar tu día para ti. Sin autocares, sin itinerarios cruzados, sin mesas compartidas a menos que tú lo pidas." },
        { k: "Sentido de lugar", v: "Una cena de castillo que podría servirse en cualquier sitio no es una cena de castillo. Buscamos la versión de algo que sólo podría suceder en ese pueblo, esa bahía, esa bodega." },
      ],
      geography_eyebrow: "— La geografía —",
      geography_title: "Cinco ciudades. Tres países.",
      geography_p: "Madrid y Sevilla para España. Roma y Campania para Italia. Lisboa para Portugal. El dossier orbita alrededor de manos en las que confiamos, en lugares a los que podemos llegar en una mañana para corregir un detalle si el tiempo, o el mundo, lo pide.",
      studio_eyebrow: "— El estudio —",
      studio_title: "Madrid · Lisboa · Roma — y en la carretera.",
      studio_p1: "Somos cinco diseñadores de viaje, fundados en 2016. Dividimos el año entre semanas de mesa en Madrid, Lisboa y Roma, y semanas de campo en las casas, cocinas, bodegas y caballerizas de nuestros socios.",
      studio_p2: "Si estás leyendo esto, probablemente ya nos conoces — o conoces a alguien que sí. El dossier completo es por presentación; este sitio es su cara pública.",
      cta_eyebrow: "Lee lo que curamos",
      cta_main: "Los veinte,",
      cta_italic: "en detalle.",
      cta_sub: "Recorre la colección por país, por ciudad o por la forma en que te gusta pasar un día.",
      cta_button: "Ver todas las experiencias",
    },
    contact_page: {
      eyebrow: "Contacto",
      title: "Escribe al estudio,",
      italic: "te respondemos.",
      sub: "Sin formularios, sin widgets de reserva. Solo una línea de tu parte al escritorio donde se guarda el dossier.",
      reach_us: "Escribe al estudio a",
      bases_label: "Sedes del estudio",
      bases: "Madrid · Lisboa · Roma",
      hours_label: "Horario del estudio",
      hours: "Lunes a viernes, 10:00 – 19:00 CET",
      note: "Respondemos cada correo — normalmente en un día laborable, a menudo antes.",
    },
    country: {
      eyebrow: "Un destino",
      by_city_eyebrow: "— Por ciudad —",
      cta_eyebrow: "De este país",
      cta_main_pre: "Todo",
      cta_main_post: ", ",
      cta_main_italic: "compuesto.",
      cta_sub: "El dossier completo de este país, en el orden en que lo viajamos.",
      cta_button: "Ver todas las experiencias",
    },
    detail: {
      back: "Volver a la colección",
      back_to_city: "Volver a",
      back_to_country: "Volver a",
      curated_by: "Curado con",
      duration: "Duración",
      perfect_for: "Ideal para",
      best_season: "Mejor época",
      highlights: "Lo que harás",
      experience_eyebrow_pre: "01 / Experiencia",
      intro_eyebrow: "— La experiencia —",
      highlights_eyebrow: "— Detalles —",
      highlights_title_pre: "Lo que la hace",
      highlights_title_italic: "singular.",
      gallery_eyebrow: "— En imágenes —",
      gallery_title_pre: "En",
      gallery_title_italic: "imágenes.",
      curated_eyebrow: "— Curada con —",
      cta_eyebrow: "La colección completa",
      cta_main: "Explora la",
      cta_italic: "colección entera.",
      cta_sub: "Veinte experiencias singulares. Tres países. Una historia cada vez.",
      cta_button: "Ver todas las experiencias",
      related_eyebrow: "— También te puede gustar —",
      related_title_pre: "Más en",
    },
    footer: {
      tagline: "Experiencias singulares en Iberia e Italia.",
      studio_eyebrow: "Estudio",
      navigate_eyebrow: "Navegar",
      contact_eyebrow: "Contacto",
      founded: "Fundado en 2016. Planeamos desde Madrid, Lisboa, Roma y en la carretera.",
      reach_us: "Escribe al estudio a",
      legal_privacy: "Privacidad",
      legal_cookies: "Cookies",
      legal_legal: "Legal",
    },
    notfound: {
      eyebrow: "Fuera del mapa",
      title: "Esta página aún no",
      italic: "existe.",
      sub: "La colección está aquí.",
      cta: "Volver a la colección",
    },
  },
};

export function LangProvider({ children }) {
  const [lang, setLang] = useState(() => {
    if (typeof window === "undefined") return "en";
    try { return localStorage.getItem("vdv-lang") || "en"; } catch { return "en"; }
  });
  useEffect(() => {
    try {
      localStorage.setItem("vdv-lang", lang);
      document.documentElement.lang = lang;
    } catch (e) {
      if (process.env.NODE_ENV === "development") {
        console.warn("[VdV][i18n] localStorage unavailable:", e);
      }
    }
  }, [lang]);

  const value = useMemo(() => ({
    lang,
    setLang,
    t: STRINGS[lang] || STRINGS.en,
    cityLabel: (slug) => cityLabel(slug, lang),
    countryLabel: (slug) => countryLabel(slug, lang),
    categoryLabel: (slug) => categoryLabel(slug, lang),
  }), [lang]);
  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export function useLang() {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error("useLang must be used within LangProvider");
  return ctx;
}
